import { BaseService } from "./base.service";
import {
  VitalsParameter,
  RecordReadingDto,
  UpdateReadingDto,
  UpdateRangeDto,
  VitalsTrackingDto,
  TrackingConfig,
  VitalsReading,
  PrescriptionVitalSuggestion,
} from "../types/vitals";

export class VitalsTrackingService extends BaseService {
  constructor() {
    super("/vitals");
  }

  async enableTracking(
    memberId: string,
    enabledParameters: VitalsParameter[],
    initialReadings: Partial<Record<VitalsParameter, Record<string, unknown>>>
  ): Promise<VitalsTrackingDto> {
    return this.post<VitalsTrackingDto>(`/tracking/${memberId}`, {
      enabledParameters,
      initialReadings,
    });
  }

  async getTrackingConfig(memberId: string): Promise<TrackingConfig> {
    return this.get<TrackingConfig>(`/tracking/${memberId}`);
  }

  async disableTracking(
    memberId: string,
    parameters: VitalsParameter[]
  ): Promise<VitalsTrackingDto> {
    return this.patch<VitalsTrackingDto>(`/tracking/${memberId}`, {
      parameters,
    });
  }

  async recordReading(
    memberId: string,
    readingDto: RecordReadingDto
  ): Promise<VitalsReading> {
    return this.post<VitalsReading>(
      `/readings/${memberId}/add-reading`,
      readingDto
    );
  }

  async updateReading(
    memberId: string,
    readingId: string,
    updateDto: UpdateReadingDto
  ): Promise<VitalsReading> {
    return this.patch<VitalsReading>(`/readings/${readingId}`, updateDto);
  }

  async deleteReading(memberId: string, readingId: string): Promise<void> {
    return this.delete<void>(`/readings/${readingId}`);
  }

  async updateCustomRange(
    memberId: string,
    rangeDto: UpdateRangeDto
  ): Promise<VitalsTrackingDto> {
    return this.put<VitalsTrackingDto>(`/ranges/${memberId}`, rangeDto);
  }

  async getReadings(
    memberId: string,
    parameter?: VitalsParameter,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    data: VitalsReading[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const body: Record<string, unknown> = { pagination: { page, pageSize } };
    if (parameter) body.filters = { parameter };
    return this.post(`/readings/${memberId}/get-readings`, body);
  }

  async getPrescriptionSuggestions(
    memberId: string
  ): Promise<PrescriptionVitalSuggestion[]> {
    return this.get<PrescriptionVitalSuggestion[]>(
      `/prescription-suggestions/${memberId}`
    );
  }

  async actionPrescriptionSuggestion(
    prescriptionId: string,
    parameter: VitalsParameter,
    action: "add" | "ignore"
  ): Promise<{ message: string }> {
    return this.post<{ message: string }>(
      `/prescription-suggestions/${prescriptionId}/action`,
      { parameter, action }
    );
  }

  async downloadVitalsPdf(
    memberId: string,
    parameter: VitalsParameter,
    startDate?: Date,
    endDate?: Date
  ): Promise<Blob> {
    const params: Record<string, string> = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if (startDate) params.startDate = startDate.toISOString();
    if (endDate) params.endDate = endDate.toISOString();
    return this.get<Blob>(`/pdf/${memberId}/${parameter}`, {
      responseType: "blob",
      params,
    });
  }
}

const vitalsTrackingService = new VitalsTrackingService();
export default vitalsTrackingService;
